// src/vdr/firmex.ts
import * as XLSX from "xlsx";
import type { IndexRow } from "./providers";

/**
 * Parse a Firmex/Datasite index file with hierarchical relative numbering.
 * 
 * Key differences from Intralinks:
 * - Column A is empty for root folders
 * - Column A shows relative path (e.g., "1.", "2.1.") not absolute
 * - Numbering resets at each root folder
 * - Trailing dot = folder, no dot = document
 * 
 * Examples:
 *   Row with A="" B="1. Transaction Documents" → folder code "1"
 *   Row with A="1." B="Marketing Materials" → folder code "1.1"
 *   Row with A="2.1." B="December 2024" → folder code "1.2.1"
 *   Row with A="1.1.1" B="Teaser" → document (skip for folders)
 */

interface ParsedRow {
  columnA: string;      // e.g., "", "1.", "2.1.", "1.1.1"
  columnB: string;      // e.g., "1. Transaction Documents", "Marketing Materials"
  columnD: string;      // Document name (for documents only)
  columnO: string;      // Complete Folder Path (e.g., "\1 Transaction Documents\2 Quals\December 2024")
  isFolder: boolean;    // true if trailing dot in column A or column A is empty
}

/**
 * Extract the root folder name from Complete Folder Path.
 * Example: "\1 Transaction Documents\2 Quals\December 2024" → "1 Transaction Documents"
 */
function extractRootFolderName(completePath: string): string {
  if (!completePath) return "";

  // Remove leading backslash and split by backslash
  const cleaned = completePath.startsWith("\\") ? completePath.slice(1) : completePath;
  const parts = cleaned.split("\\");

  // Return the first part (root folder)
  return parts[0] || "";
}

// Parse a single Excel/CSV row
function parseRow(row: any[]): ParsedRow | null {
  const colA = String(row[0] ?? "").trim();
  const colB = String(row[1] ?? "").trim();
  const colD = String(row[3] ?? "").trim(); // Column D is index 3 (0-based) - document names
  const colO = String(row[14] ?? "").trim(); // Column O is index 14 (0-based)

  // Skip header rows and empty rows
  if (!colB || colB.toLowerCase() === "folder") return null;

  // Empty column A or trailing dot = folder
  const isFolder = colA === "" || colA.endsWith(".");

  return { columnA: colA, columnB: colB, columnD: colD, columnO: colO, isFolder };
}

/**
 * Build absolute folder codes from relative numbering.
 * 
 * Algorithm:
 * 1. Track current root number (1, 2, 3...)
 * 2. Track current path segments as we descend
 * 3. When we see empty column A → new root folder (reset path)
 * 4. When we see "N." → child at that position
 * 5. When we see "N.M." → grandchild, etc.
 */
export async function parseIndexFile(file: File): Promise<IndexRow[]> {
  const ext = file.name.toLowerCase().split(".").pop();
  console.log(`🧾 [firmex] parseIndexFile: name="${file.name}" ext="${ext}"`);

  // Read file as 2D array
  let table: any[][];
  if (ext === "csv") {
    const text = await file.text();
    table = text.split(/\r?\n/)
      .filter(l => l.trim())
      .map(line => safeSplit(line));
  } else if (ext === "xlsx" || ext === "xls") {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    table = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
  } else {
    throw new Error(`Unsupported file type: ${ext}`);
  }

  console.log(`   ↳ raw rows: ${table.length}`);

  const folders: IndexRow[] = [];
  const documents: IndexRow[] = []; // Store document rows too
  let currentRoot = 0;              // 1, 2, 3... (increments on each empty column A)
  let currentRootName = "";         // Track the root folder name from Column O
  let pathStack: number[] = [];     // Current path as array of numbers [1, 2, 1] = "1.2.1"

  for (let i = 0; i < table.length; i++) {
    const parsed = parseRow(table[i]);
    if (!parsed) continue;

    const { columnA, columnB, columnD, columnO, isFolder } = parsed;

    // Handle folders
    if (isFolder) {
      // Case 1: Empty column A = new root folder
      if (columnA === "") {
        currentRoot++;
        pathStack = [currentRoot];

        const code = String(currentRoot);
        // Strip leading code from title (e.g., "1. Transaction Documents" → "Transaction Documents")
        const title = columnB.replace(/^\d+(\.\d+)*\.\s+/, "").trim() || columnB;

        // Extract root folder name from Column O
        // For root folders, Column O might be just "\1. Transaction Documents" or empty
        // Use Column B as fallback (which has the root folder name)
        const rootNameFromO = extractRootFolderName(columnO);
        currentRootName = rootNameFromO || columnB;

        console.log(`   📁 ROOT: code="${code}" title="${title}" columnO="${columnO}" rootName="${currentRootName}"`);
        folders.push({
          code,
          title,
          meta: { rootFolderName: currentRootName }
        });
        continue;
      }

      // Case 2: Column A has trailing dot = subfolder
      // Extract the segments: "2.1." → [2, 1]
      const segments = columnA
        .replace(/\.$/, "") // Remove trailing dot
        .split(".")
        .filter(Boolean)
        .map(s => parseInt(s, 10));

      if (segments.length === 0) continue;

      // Adjust path stack to match this depth
      const targetDepth = segments.length;

      // Pop stack until we're at the right parent level
      while (pathStack.length > targetDepth) {
        pathStack.pop();
      }

      // Build new path: keep root + parent segments, then add new segment
      const lastSegment = segments[segments.length - 1];
      pathStack = [...pathStack.slice(0, targetDepth), lastSegment];

      const code = pathStack.join(".");
      // Strip leading code from title (e.g., "4. Operations" → "Operations")
      const title = columnB.replace(/^\d+(\.\d+)*\.\s+/, "").trim() || columnB;

      // For child folders, extract root from their own Column O (which has full path)
      // This is more reliable than using currentRootName
      const childRootName = extractRootFolderName(columnO) || currentRootName;

      console.log(`   📁 CHILD: columnA="${columnA}" → code="${code}" title="${title}" columnO="${columnO}" rootName="${childRootName}"`);
      folders.push({
        code,
        title,
        meta: { rootFolderName: childRootName }
      });
    } else {
      // Handle documents - store their code, root folder, and complete path for routing
      const docCode = columnA;
      const docTitle = columnD || columnB; // Use Column D for document names, fall back to Column B
      const rootName = extractRootFolderName(columnO);

      // Always add documents (even without code) for path-based routing
      console.log(`   📄 DOC: code="${docCode}" title="${docTitle}" path="${columnO}"`);
      documents.push({
        code: docCode || docTitle, // Use title as code if no code
        title: docTitle,
        meta: {
          rootFolderName: rootName,
          isDocument: true,
          completePath: columnO // Store full path for navigation
        }
      });
    }
  }

  console.log(`   ↳ parsed folders: ${folders.length}`);
  console.log(`   ↳ parsed documents: ${documents.length}`);
  if (folders.length > 0) {
    const sample = folders.slice(0, Math.min(5, folders.length));
    console.log("   ↳ folder sample:", sample);
  }

  // Return ALL rows (folders + documents)
  // Folder creation will filter by !meta.isDocument
  // File routing will use document rows to determine correct root folder
  return [...folders, ...documents];
}

/**
 * Helper for folder matching in SharePoint.
 * Unlike Intralinks, Firmex uses exact hierarchical codes.
 * No special aliasing needed - just match the code as-is.
 */
export function getRootFolderNameCandidatesFromCode(code: string): string[] {
  // For Firmex, codes are exact - no ".0" aliasing
  return [code];
}

// Simple CSV splitter (handles quoted fields)
function safeSplit(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}